import useUserStore from '@/stores/useUserStore';
import { getProfile } from '@/requests/useProfile';
import { syncSubjectToCookie } from '@/utils/subject';
import anewSignIn from '@/utils/anewSignIn';
import Axios from '@/utils/Axios';
import Logger from '@/utils/Logger';
import { resumeProfileTextbook } from '@/utils/setProfileTextbook';
import { getAuthToken } from '@/utils/authToken';
import getDomainPlatform, { DomainPlatformValue } from './getDomainPlatform';

async function loadSchool(isCurrentSession: () => boolean) {
  const { data } = await Axios.get<
    {
      current: boolean;
      id: number;
      name: string;
      role: string;
    }[]
  >('/teachers/schools');

  if (!isCurrentSession()) return;
  useUserStore.setState({
    schools: data.map((v) => ({
      id: String(v.id),
      name: v.name,
    })),
    currentSchoolId: String(data.find((v) => v.current)?.id || ''),
  });
}

let alreadyLoadedUserProfile = false;
let loadedFsToken: ReturnType<typeof getAuthToken>;

/**
 * 加载教师 profile 与学校列表，写入 useUserStore（订阅负责同步 user_store cookie）。
 *
 * 失败语义：getProfile 返回 401/403 视为会话失效跳登录；其余失败（网络错误/5xx）
 * 与 loadSchool 失败仅记录日志、保留会话，避免偶发接口失败把用户踢出登录。
 */
export default async function loadUserProfile() {
  const token = getAuthToken();
  const isFs = getDomainPlatform() === DomainPlatformValue.Fs;
  const isCurrentSession = () => !isFs || getAuthToken() === token;
  if (
    alreadyLoadedUserProfile &&
    useUserStore.getState().isFilled() &&
    (!isFs || loadedFsToken === token)
  )
    return;

  try {
    const profile = await getProfile();
    if (!isCurrentSession()) return;
    useUserStore.setState({
      id: String(profile.id),
      name: profile.name,
      prefer: profile.prefer || false,
      unit: profile.unit,
      section: profile.section,
    });

    // FS：外壳以账号当前学科作为站点学科。app.tsx 的 render 会等待本函数完成后再首屏渲染，
    // 这里直写 id 消灭冷启动空窗；宿主侧换账号（token 变化）不经我方登出，同样以这份档案为准，
    // 没有当前学科就清空，避免沿用上一账号的学科。名称由 FsCurrentSubjectSync 用学科字典补齐
    if (isFs) {
      const id = profile.subject == null ? undefined : String(profile.subject);
      useUserStore.setState((state) => ({
        currentSubject:
          id == null
            ? undefined
            : {
                id,
                name:
                  state.currentSubject?.id === id
                    ? state.currentSubject.name
                    : '',
              },
      }));
    }

    // FS：账号只有一条教材档案、还没有「当前」标记时补上标记（此时学科、单元一定有值），
    // 之后这个账号在别的平台多出其他学科的档案，FS 也不会丢失当前所在的学科
    const marking =
      profile.isSubjectCurrent === false
        ? resumeProfileTextbook({
            subjectId: profile.subject!,
            unitId: profile.unit!,
            sectionId: profile.section,
          })
            .then(() => true)
            .catch((error) => {
              Logger.debug(error, '标记当前所在学科失败');
              return false;
            })
        : Promise.resolve(true);

    syncSubjectToCookie();

    try {
      await loadSchool(isCurrentSession);
    } catch (schoolError) {
      Logger.debug(schoolError, '加载学校列表失败');
    }

    if (!(await marking) || !isCurrentSession()) return;
    alreadyLoadedUserProfile = true;
    if (isFs) loadedFsToken = token;
  } catch (e) {
    if (!isCurrentSession()) return;
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 403) {
      anewSignIn('身份验证失败，请重新登录');
      return;
    }

    Logger.debug(e, '加载用户信息失败');
  }
}
